import React, { useState, useRef, useEffect } from "react";
import { Modal, Typography, Flex, Button, Avatar, message, Upload } from "antd";
import {
  CameraOutlined,
  DeleteOutlined,
  UploadOutlined,
  UserOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setSession } from "../../../redux/slices/sessionSlice";
import { setCompanyInfo } from "../../../redux/slices/executorSlice";
import { updateCompanyProfilePhoto } from "../../../services/organizationService";
import { updateEmployeeProfilePhoto } from "../../../services/employeeService";

const { Title, Text, Paragraph } = Typography;

/**
 * Resizes and converts image file to compressed base64 data URL
 */
function processImageFile(file, maxWidth = 400, maxHeight = 400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("Failed to load image for processing."));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read image file."));
    reader.readAsDataURL(file);
  });
}

function ProfilePhotoModal({
  open,
  onCancel,
  currentPhoto = "",
  userName = "User",
  companyName = "DealVoice",
  role = "company_admin",
  companyCode = "",
  employeeId = "",
  userInfo = {},
  onSuccess,
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [previewPhoto, setPreviewPhoto] = useState(currentPhoto);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      setPreviewPhoto(currentPhoto || "");
      setHasChanges(false);
    }
  }, [open, currentPhoto]);

  const getInitials = (name) => {
    if (!name) return "DV";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error("Please upload an image file (PNG, JPG, JPEG, WebP).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error("Image file size should be less than 5MB.");
      return;
    }

    try {
      const dataUrl = await processImageFile(file);
      setPreviewPhoto(dataUrl);
      setHasChanges(true);
    } catch (err) {
      console.error(err);
      message.error("Failed to process image file.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemovePhoto = () => {
    setPreviewPhoto("");
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const isEmployee = role === "employee" || role === "sales_rep";
      let res;

      if (isEmployee) {
        const targetEmpId = employeeId || userInfo?.id || userInfo?._id;
        res = await updateEmployeeProfilePhoto(targetEmpId, previewPhoto, companyCode);
      } else {
        res = await updateCompanyProfilePhoto(companyCode, previewPhoto);
      }

      if (res?.success !== false) {
        // Update Redux state immediately
        const updatedUserInfo = {
          ...userInfo,
          profilePhoto: previewPhoto,
        };

        dispatch(
          setSession({
            userInfo: updatedUserInfo,
          })
        );

        if (!isEmployee) {
          dispatch(
            setCompanyInfo({
              companyCode,
              profilePhoto: previewPhoto,
            })
          );
        }

        message.success("Profile photo updated successfully!");
        onSuccess?.(previewPhoto);
        onCancel();
      } else {
        message.error(res?.message || "Failed to update profile photo.");
      }
    } catch (err) {
      console.error("Profile photo save error:", err);
      message.error("Failed to save profile photo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => !loading && onCancel()}
      footer={null}
      centered
      width={420}
      styles={{
        body: {
          padding: "24px 20px 20px",
          textAlign: "center",
        },
      }}
    >
      <Flex vertical align="center" gap={18}>
        {/* Modal Heading */}
        <Flex vertical align="center" gap={4}>
          <Title level={4} style={{ margin: 0, color: "#0f172a", fontWeight: 700 }}>
            Profile Photo
          </Title>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: "0.85rem", color: "#64748b" }}>
            Upload a clear photo for your {role === "employee" ? "sales rep" : "company admin"} profile
          </Paragraph>
        </Flex>

        {/* Large Avatar Preview with edit overlay */}
        <div style={{ position: "relative", marginTop: 4 }}>
          <Avatar
            size={120}
            src={previewPhoto || null}
            style={{
              backgroundColor: previewPhoto ? "transparent" : "#2459d3",
              fontSize: "2.2rem",
              fontWeight: 700,
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.08)",
              border: "3px solid #ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getInitials(userName || companyName)}
          </Avatar>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              position: "absolute",
              bottom: 2,
              right: 2,
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "#2563eb",
              color: "#ffffff",
              border: "2px solid #ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.35)",
              transition: "transform 0.15s ease",
            }}
            title="Upload photo"
          >
            <CameraOutlined style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/png,image/jpeg,image/jpg,image/webp"
          style={{ display: "none" }}
        />

        {/* Upload & Remove Action Buttons */}
        <Flex gap={10} justify="center" wrap="wrap">
          <Button
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            {previewPhoto ? "Change Photo" : "Upload Photo"}
          </Button>

          {previewPhoto && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemovePhoto}
              style={{ borderRadius: 8, fontWeight: 500 }}
            >
              Remove
            </Button>
          )}
        </Flex>

        {/* User Capsule */}
        <div
          style={{
            width: "100%",
            background: "#f8fafc",
            border: "1px solid #f1f5f9",
            borderRadius: 10,
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            textAlign: "left",
          }}
        >
          <UserOutlined style={{ color: "#2563eb", fontSize: 16 }} />
          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: "0.82rem", color: "#1e293b" }} ellipsis>
              {userName}
            </Text>
            <Text type="secondary" style={{ fontSize: "0.72rem", color: "#64748b" }} ellipsis>
              {companyName} {companyCode ? `• #${companyCode}` : ""}
            </Text>
          </Flex>
        </div>

        {/* Footer Actions */}
        <Flex justify="center" gap={12} style={{ width: "100%", marginTop: 4 }}>
          <Button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={loading}
            disabled={!hasChanges}
            onClick={handleSave}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 8,
              fontWeight: 600,
              background: "#2563eb",
              borderColor: "#2563eb",
            }}
          >
            Save Photo
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}

export default ProfilePhotoModal;
